// Changing a condition report's rating scale after it started (#473; MJ,
// 2026-10-23). Each clear and the scale itself save through the outbox like
// any other edit, so it works offline. The rules are condition.ts's.
import { patchFields, type DeviceRecord } from './device-db';
import { db, queue } from './device-store';
import { CLEARED, ratedObservations, scaleChangeFields, staleRatings, visitMustGoLast } from './condition';
import { syncNow } from './outbox';

/** Move the visit's queued save behind every other (condition.ts visitMustGoLast); a sync already running skips the old place (outbox.ts). */
async function sendVisitLast(visit: DeviceRecord): Promise<void> {
  const tx = (await db()).transaction(['outbox'], 'readwrite');
  const entries = await tx.objectStore('outbox').getAll();
  if (visitMustGoLast(entries, visit.id)) {
    for (const e of entries) if (e.targetId === visit.id && e.kind === 'record') await tx.objectStore('outbox').delete(e.seq!);
    await queue(tx, { kind: 'record', targetId: visit.id, jobId: visit.jobId });
  }
  await tx.done;
}

/**
 * Change the report's scale, clearing every rating first: only the rating
 * values go, defects, notes and photos stay. Every rating's save goes before
 * the scale's, and a rating made after it goes behind both.
 */
export async function changeScale(visit: DeviceRecord, records: readonly DeviceRecord[], scaleLabel: string): Promise<void> {
  for (const rating of ratedObservations(records)) await patchFields(rating.id, CLEARED);
  await sendVisitLast(visit);
  await patchFields(visit.id, (stored) => scaleChangeFields(stored, scaleLabel));
  void syncNow();
}

/** Clear this device's ratings made on a scale the report no longer uses (condition.ts staleRatings). Returns how many. */
export async function clearStaleRatings(visit: DeviceRecord, records: readonly DeviceRecord[]): Promise<number> {
  const stale = staleRatings(records, visit.fields.hig_ratingscale);
  for (const rating of stale) await patchFields(rating.id, CLEARED);
  return stale.length;
}
